import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { PlatformChatSession } from '@liks/backend-svc-api/esm/models/index';
import { platformChatService } from '../../../services/PlatformChatService';
import { chatQueryKeys } from '../chatQueryKeys';

export function useCreatePlatformChatSessionMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (title: string) => platformChatService.createSession(title),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: chatQueryKeys.sessions(),
      });
    },
  });
}

export function useSetPlatformChatSessionSystemPromptMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      sessionId,
      systemPrompt,
    }: {
      sessionId: string;
      systemPrompt: string;
    }) => platformChatService.setSessionSystemPrompt(sessionId, systemPrompt),
    onSuccess: (_result, variables) => {
      void queryClient.invalidateQueries({
        queryKey: chatQueryKeys.session(variables.sessionId),
      });
      void queryClient.invalidateQueries({
        queryKey: chatQueryKeys.sessions(),
      });
    },
  });
}

export function useRenamePlatformChatSessionMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      session,
      title,
    }: {
      session: PlatformChatSession;
      title: string;
    }) => platformChatService.renameSession(session, title),
    onSuccess: (_result, variables) => {
      if (typeof variables.session.id === 'string') {
        void queryClient.invalidateQueries({
          queryKey: chatQueryKeys.session(variables.session.id),
        });
      }
      void queryClient.invalidateQueries({
        queryKey: chatQueryKeys.sessions(),
      });
    },
  });
}

export function useDeletePlatformChatSessionMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (sessionId: string) =>
      platformChatService.deleteSession(sessionId),
    onSuccess: (_result, sessionId) => {
      void queryClient.invalidateQueries({
        queryKey: chatQueryKeys.sessions(),
      });
      queryClient.removeQueries({ queryKey: chatQueryKeys.session(sessionId) });
      queryClient.removeQueries({
        queryKey: chatQueryKeys.messages(sessionId),
      });
      queryClient.removeQueries({
        queryKey: chatQueryKeys.participants(sessionId),
      });
    },
  });
}

export function usePinPlatformChatSessionMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (sessionId: string) =>
      platformChatService.pinSession(sessionId),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: chatQueryKeys.sessions(),
      });
    },
  });
}

export function useUnpinPlatformChatSessionMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (sessionId: string) =>
      platformChatService.unpinSession(sessionId),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: chatQueryKeys.sessions(),
      });
    },
  });
}

export function useInvitePlatformChatParticipantMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      sessionId,
      userId,
    }: {
      sessionId: string;
      userId: string;
    }) => platformChatService.inviteParticipant(sessionId, userId),
    onSuccess: (_result, variables) => {
      void queryClient.invalidateQueries({
        queryKey: chatQueryKeys.participants(variables.sessionId),
      });
      void queryClient.invalidateQueries({
        queryKey: chatQueryKeys.sessions(),
      });
    },
  });
}

export function useRemovePlatformChatParticipantMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      sessionId,
      participantId,
    }: {
      sessionId: string;
      participantId: string;
    }) => platformChatService.removeParticipant(sessionId, participantId),
    onSuccess: (_result, variables) => {
      void queryClient.invalidateQueries({
        queryKey: chatQueryKeys.participants(variables.sessionId),
      });
      void queryClient.invalidateQueries({
        queryKey: chatQueryKeys.sessions(),
      });
    },
  });
}

export function useTransferPlatformChatOwnershipMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      sessionId,
      newOwnerUserId,
    }: {
      sessionId: string;
      newOwnerUserId: string;
    }) => platformChatService.transferOwnership(sessionId, newOwnerUserId),
    onSuccess: (_result, variables) => {
      void queryClient.invalidateQueries({
        queryKey: chatQueryKeys.sessions(),
      });
      void queryClient.invalidateQueries({
        queryKey: chatQueryKeys.participants(variables.sessionId),
      });
    },
  });
}

export function useLinkPlatformChatKnowledgeBaseMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      sessionId,
      knowledgeBaseId,
    }: {
      sessionId: string;
      knowledgeBaseId: string;
    }) => platformChatService.linkKnowledgeBase(sessionId, knowledgeBaseId),
    onSuccess: (_result, variables) => {
      void queryClient.invalidateQueries({
        queryKey: chatQueryKeys.linkedKnowledgeBases(variables.sessionId),
      });
    },
  });
}

export function useUnlinkPlatformChatKnowledgeBaseMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      sessionId,
      linkId,
    }: {
      sessionId: string;
      linkId: string;
    }) => platformChatService.unlinkKnowledgeBase(sessionId, linkId),
    onSuccess: (_result, variables) => {
      void queryClient.invalidateQueries({
        queryKey: chatQueryKeys.linkedKnowledgeBases(variables.sessionId),
      });
    },
  });
}

export function useTranscribePlatformChatAudioMutation() {
  return useMutation({
    mutationFn: (file: File) => platformChatService.transcribeAudio(file),
  });
}
